import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { usersApi } from "@/api";
import type { UsersFilter } from "@/api";
import { QUERY_KEYS } from "@/constants";
import type { UserRole } from "@/types";

export const useUsers = (filter: UsersFilter) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USERS, filter],
    queryFn: () => usersApi.getAll(filter),
    // Keyingisi yuklanguncha oldingi sahifa ko'rinib turadi — miltillash bo'lmaydi.
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });
};

/**
 * Ro'yxat tepasidagi ko'rsatkichlar uchun sonlar.
 *
 * Backend'da alohida "statistika" endpoint'i YO'Q, shuning uchun har bir
 * kesim uchun `limit: 1` bilan so'rov yuboriladi va faqat `meta.total`
 * o'qiladi: bitta yozuv keladi, son esa haqiqiy. Ular parallel ketadi va
 * kesh kaliti oddiy ro'yxat kalitidan ajralib turadi.
 */
const userOptions = (role: UsersFilter["role"], input: string) => ({
  queryKey: [QUERY_KEYS.GET_USERS, "filter-options", role ?? "all", input],
  queryFn: async () => {
    const res = await usersApi.getAll({
      page: 1,
      limit: 20,
      role,
      search: input.trim() || undefined,
    });
    return res.data.map(user => ({
      label: `${user.firstName} ${user.lastName}`.trim() || user.email,
      value: user.id,
    }));
  },
});

/**
 * Jadval sarlavhasidagi "O'qituvchi" filtri uchun variantlar.
 *
 * Hook emas — `meta.filterOptionsAsync` shakli: so'rov faqat filtr
 * popoveri ochilganda ketadi va faqat o'qituvchilar orasidan qidiradi.
 */
export const teacherFilterOptions = (input: string) => userOptions("teacher", input);

/** Faqat talabalar orasidan (masalan, baho o'zgarishlaridagi "Talaba" ustuni). */
export const studentFilterOptions = (input: string) => userOptions("student", input);

/** Istalgan roldagi odam (masalan, faollik jurnalidagi "Kim" ustuni). */
export const userFilterOptions = (input: string) => userOptions(undefined, input);

/** URL'dan kelgan foydalanuvchi id'sining nomi (`meta.filterOptionLabel`). */
export const userFilterLabel = (value: string) => ({
  queryKey: [QUERY_KEYS.GET_USERS, "filter-label", value],
  queryFn: async () => {
    const id = Number(value);
    if (!Number.isInteger(id) || id <= 0) return null;
    const res = await usersApi.getById(id);
    return `${res.data.firstName} ${res.data.lastName}`.trim() || res.data.email;
  },
});

export const useUserCounts = () => {
  const slices = [
    { key: "total", filter: {} as UsersFilter },
    { key: "active", filter: { status: "active" } as UsersFilter },
    { key: "blocked", filter: { status: "blocked" } as UsersFilter },
    { key: "admin", filter: { role: "admin" } as UsersFilter },
    { key: "moderator", filter: { role: "moderator" } as UsersFilter },
    { key: "teacher", filter: { role: "teacher" } as UsersFilter },
    { key: "student", filter: { role: "student" } as UsersFilter },
  ] as const;

  const results = useQueries({
    queries: slices.map(slice => ({
      queryKey: [QUERY_KEYS.GET_USERS, "count", slice.key],
      queryFn: () => usersApi.getAll({ ...slice.filter, page: 1, limit: 1 }),
      select: (res: Awaited<ReturnType<typeof usersApi.getAll>>) => res.meta.total,
    })),
  });

  const counts = {} as Record<(typeof slices)[number]["key"], number | undefined>;
  slices.forEach((slice, index) => {
    counts[slice.key] = results[index]?.data;
  });

  return {
    counts,
    isLoading: results.some(result => result.isLoading),
    isError: results.some(result => result.isError),
  };
};

/** Aloqa ma'lumotlari: xabar kanallari va talabaning ota-onasi. */
export const useUserContacts = (id: number, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_USER_CONTACTS, id],
    queryFn: () => usersApi.getContacts(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
    select: res => res.data,
  });

export const useChangeUserRole = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: UserRole }) => usersApi.changeRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_USERS] });
      toast.success(t("USERS.ROLE_UPDATED"));
    },
  });
};

export const useBlockUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersApi.block(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_USERS] });
      toast.success(t("USERS.BLOCKED"));
    },
  });
};

export const useUnblockUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersApi.unblock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_USERS] });
      toast.success(t("USERS.UNBLOCKED"));
    },
  });
};
